import { useEffect, useRef, useState, type FormEvent } from "react";
import type { EpisodeSummary, HeroPricing } from "@scrile/contracts";
import { Avatar } from "../components/Avatar";
import { EpisodeList } from "../components/EpisodeList";
import { EpisodesBlock } from "../components/EpisodesBlock";
import { Icon, type IconName } from "../components/Icon";
import { Link } from "../components/Link";
import { t, tn, type MessageKey } from "../i18n";
import { hasAccess, isSubscribed, resumeTarget, type EpisodeState } from "../lib/episodes";
import { formatCents, monthlyEquivalentCents, trialDaysFor } from "../lib/money";
import { episodeKey, loadPosition } from "../lib/positions";
import { href, navigate } from "../lib/router";
import { Scrubber } from "../player/Scrubber";
import { Transcript } from "../player/Transcript";
import { usePlayer, usePlayerActions } from "../player/PlayerContext";
import { useHeroData } from "../state/heroData";

type PreviewMessage = { from: "theo" | "you"; text: string };

const THEO_INITIAL: readonly PreviewMessage[] = [
  { from: "theo", text: "Still awake?" },
  { from: "theo", text: "I'm inside the Orpheum. Projection room. There are only supposed to be five reels here." },
  { from: "theo", text: "I'm holding a sixth." },
];

const THEO_REPLIES = [
  "I know. I shouldn't be texting someone from a condemned cinema at one in the morning. And yet.",
  "The label says “private — do not screen.” My grandfather's handwriting. I'd know it anywhere.",
  "All right. I'm going to open it. Stay with me a minute — this place sounds different when someone else is listening.",
  "The film can is colder than the room. That makes no sense, but my hands are shaking anyway.",
  "There's a date on the leader: October 14, 1987. The night the Orpheum closed.",
  "And a note from the projectionist: start after the last guest leaves. Apparently that's us.",
  "You're right — photos first. If this turns to dust, I want proof it existed.",
  "Three frames are clear enough to see. A woman on the stage, looking straight at the booth.",
  "It's my mother. She would have been nineteen. She never told me she came here.",
  "There is someone behind her, just outside the light. I can only see his hand on the curtain.",
  "Wait. He is wearing the old silver usher's ring. We found one exactly like it under the balcony last week.",
  "No, it wasn't my grandfather's. He hated the ushers. Said they knew every secret in the building.",
  "The credits call him Elias Vale. I know that name from the blueprints in the basement.",
  "Vale designed the hidden corridor behind screen two. The corridor the city inspector says does not exist.",
  "There is writing beneath the leader. Tiny pencil, almost rubbed away. Give me a second.",
  "It says: house lights stay down until she chooses. I don't know who 'she' is yet.",
  "This wasn't a movie for an audience. It was a message for one person.",
  "The booth door just moved. I heard the latch, not the wind.",
  "Probably the draft. Tell me it's the draft anyway. I could use the lie.",
  "Good. I locked it. And yes, I checked behind me twice because you were about to ask.",
  "There is an address scratched into the reel case — 41 Mercer, apartment 6.",
  "That's two streets from here. The building is still standing; I passed it this morning.",
  "Tomorrow night I'll knock. Tonight I need to copy every frame before this projector eats the film.",
  "You stayed. Most people tell me to call the police or go home. You just stayed.",
  "Come back tomorrow and I'll show you what was hidden in frame 214. I think the Orpheum has been waiting for both of us.",
] as const;

/**
 * Общий шаблон остаётся для всех героев; Luca и Theo получают launch-лендинги,
 * построенные на тех же API, плеере, эпизодах, ценах и маршрутах оплаты.
 */
export function HeroPage() {
  const { hero, me, episodes } = useHeroData();
  const player = usePlayer();
  const actions = usePlayerActions();
  const isAudio = hero.format === "audio";
  const access = hasAccess(me);
  const subscribed = isSubscribed(me);
  const { pricing } = hero;
  const trialDays = trialDaysFor(pricing, me ? me.trialEligible : null);
  const season = hero.currentSeason;
  const episodeCount = season?.episodes ?? episodes.length;
  const luca = hero.slug === "chef" || hero.slug === "luca" || hero.name.toLowerCase() === "luca";
  const theo = hero.slug === "theo" || hero.name.toLowerCase() === "theo";

  const finished = (ordinal: number) => loadPosition(episodeKey(hero.slug, ordinal))?.finished === true;
  const pilot = episodes.find((episode) => episode.free && episode.available) ?? null;
  const resume = resumeTarget(episodes, finished);
  const pilotDone = pilot !== null && episodes.filter((episode) => episode.free).every((episode) => finished(episode.ordinal));
  const firstLocked = episodes.find((episode) => episode.locked && episode.releasedAt !== null) ?? null;

  const [theoMessages, setTheoMessages] = useState<PreviewMessage[]>([...THEO_INITIAL]);
  const [theoSent, setTheoSent] = useState(0);
  const [theoDraft, setTheoDraft] = useState("");

  function play(episode: EpisodeSummary) {
    void actions.playEpisode(episode.ordinal);
    navigate(href.listen(episode.ordinal));
  }

  function onSelect(episode: EpisodeSummary, state: EpisodeState) {
    if (state === "locked") navigate(href.plans({ episode: episode.ordinal }));
    else if (state === "free" || state === "available") play(episode);
  }

  const pilotActive = pilot !== null && player.track?.kind === "episode" && player.track.ordinal === pilot.ordinal;
  const pilotPlaying = pilotActive && player.status === "playing";
  const pilotLoading = pilot !== null && player.requested?.kind === "episode" && player.requested.ordinal === pilot.ordinal && player.status === "loading";

  function togglePilot() {
    if (!pilot) return;
    if (pilotActive) actions.toggle();
    else void actions.playEpisode(pilot.ordinal);
  }

  function sendTheoPreview(text: string) {
    const message = text.trim();
    if (!message || theoSent >= THEO_REPLIES.length) return;
    const reply = THEO_REPLIES[theoSent];
    if (!reply) return;
    setTheoMessages((current) => [...current, { from: "you", text: message }, { from: "theo", text: reply }]);
    setTheoSent((current) => current + 1);
    setTheoDraft("");
  }

  function submitTheoPreview(event: FormEvent) {
    event.preventDefault();
    sendTheoPreview(theoDraft);
  }

  const trailerPlaying = player.track?.kind === "trailer" && player.status === "playing";
  const perMonth = formatCents(monthlyEquivalentCents(pricing.yearlyCents), pricing.currency);

  let primary: { label: string; onClick: () => void };
  if (!isAudio) {
    primary = me
      ? { label: access ? t("hero.continueChat") : t("hero.openChat"), onClick: () => navigate(href.chat()) }
      : { label: t("hero.startChat"), onClick: () => navigate(href.join(href.chat())) };
  } else if (access && resume) {
    primary = { label: t("hero.continueEpisode", { n: resume.ordinal }), onClick: () => play(resume) };
  } else if (pilot && !pilotDone) {
    primary = { label: t("hero.startPilot"), onClick: luca ? togglePilot : () => play(pilot) };
  } else {
    primary = {
      label: trialDays > 0 ? t("paywall.cta.trial", { days: trialDays }) : t("paywall.cta.subscribe"),
      onClick: () => navigate(href.plans({ episode: firstLocked?.ordinal ?? null })),
    };
  }

  if (luca && isAudio) {
    return <LucaLanding
      episodes={episodes}
      episodeCount={episodeCount}
      pilot={pilot}
      pilotActive={pilotActive}
      pilotPlaying={pilotPlaying}
      pilotLoading={pilotLoading}
      subscribed={subscribed}
      trialDays={trialDays}
      onTogglePilot={togglePilot}
      onSelect={onSelect}
    />;
  }

  if (theo && !isAudio) {
    const repliesLeft = Math.max(0, THEO_REPLIES.length - theoSent);
    return <TheoLanding
      messages={theoMessages}
      repliesLeft={repliesLeft}
      draft={theoDraft}
      subscribed={subscribed}
      trialDays={trialDays}
      onDraft={setTheoDraft}
      onReply={sendTheoPreview}
      onSubmit={submitTheoPreview}
      onOpenConversation={primary.onClick}
    />;
  }

  const showPilotNote = isAudio && !access && pilot !== null && !pilotDone;
  const showChatNote = !isAudio && !access && hero.freeEpisodes > 0;

  return (
    <main className="hero-page">
      <section className={`cover${hero.theme.coverUrl ? "" : " cover--generated"}`} aria-hidden="true">
        {hero.theme.coverUrl ? <img className="cover__img" src={hero.theme.coverUrl} alt="" decoding="async" /> : <div className="cover__art" />}
        <div className="cover__fade" />
      </section>

      <div className="hero-layout container">
        <section className="hero-head">
          <Avatar src={hero.theme.portraitUrl} name={hero.name} size={92} className="hero-head__portrait" />
          <p className="eyebrow">{isAudio ? t("hero.format.audio") : t("hero.format.chat")}{isAudio && episodeCount > 0 && <> · {tn("hero.episodesCount", episodeCount)}</>}</p>
          <h1 className="hero-head__name">{hero.name}</h1>
          <p className="hero-head__tagline">{hero.tagline}</p>
          <div className="hero-head__actions">
            <button type="button" className="btn btn--primary btn--lg btn--block" onClick={primary.onClick}>{!access && isAudio && pilot && !pilotDone && <Icon name="play" size={18} />}{primary.label}</button>
            {showPilotNote && <p className="hint hero-head__note">{tn("hero.pilotNote", hero.freeEpisodes)}</p>}
            {showChatNote && <p className="hint hero-head__note">{tn("hero.chatNote", hero.freeEpisodes, { hero: hero.name })}</p>}
            {(isAudio ? !subscribed && !pilotDone : !subscribed) && <Link to={href.plans()} className="btn btn--quiet btn--block">{trialDays > 0 ? t("hero.trialLink", { days: trialDays }) : t("hero.subscribeLink")}</Link>}
          </div>
          {hero.trailerUrl && <Trailer url={hero.trailerUrl} poster={hero.theme.coverUrl} playing={trailerPlaying} onToggle={() => actions.playTrailer(hero.trailerUrl ?? "", hero.name)} />}
          {isAudio && <ul className="chips" aria-label={t("hero.weekly.title")}><li className="chip">{t("hero.feature.length")}</li><li className="chip">{t("hero.feature.transcript")}</li><li className="chip">{t("hero.feature.sleep")}</li><li className="chip">{t("hero.feature.name")}</li></ul>}
          <p className="hero-head__description">{hero.description}</p>
        </section>

        {isAudio && <section className="panel hero-episodes" aria-labelledby="episodes-title"><div className="section-head"><div><h2 id="episodes-title">{season ? t("hero.season", { n: season.ordinal }) : t("hero.episodes")}</h2>{season && <p className="season-title">{season.title}</p>}</div>{episodeCount > 0 && <span className="muted">{tn("hero.episodesCount", episodeCount)}</span>}</div><EpisodesBlock><EpisodeList episodes={episodes} heroSlug={hero.slug} timeZone={me?.timezone} currentOrdinal={player.track?.kind === "episode" ? player.track.ordinal : null} playing={player.status === "playing"} onSelect={onSelect} /></EpisodesBlock></section>}
      </div>

      <section className="container weekly" aria-labelledby="weekly-title"><h2 id="weekly-title">{t("hero.weekly.title")}</h2><div className="weekly__grid">{(isAudio ? AUDIO_PERKS : CHAT_PERKS).map((perk) => <article className="perk" key={perk.title}><span className="perk__icon"><Icon name={perk.icon} size={22} /></span><h3 className="perk__title">{t(perk.title)}</h3><p className="perk__body">{t(perk.body, { hero: hero.name })}</p></article>)}</div></section>

      {!subscribed && <section className="container"><div className="pricing-card"><div><p className="pricing-card__title">{trialDays > 0 ? t("hero.pricing.title", { days: trialDays, perMonth }) : t("hero.pricing.titleNoTrial", { perMonth })}</p><p className="pricing-card__body">{t("hero.pricing.body", { yearly: formatCents(pricing.yearlyCents, pricing.currency), monthly: formatCents(pricing.monthlyCents, pricing.currency) })}</p></div><Link to={href.plans()} className="btn btn--primary">{t("hero.pricing.cta")}</Link></div></section>}

      <footer className="container site-footer"><a href={hero.creator.url} className="site-footer__creator">{t("hero.madeBy", { creator: hero.creator.name })}</a><span className="muted">{t("app.onScrile")}</span></footer>
    </main>
  );
}

function LucaLanding({
  episodes,
  episodeCount,
  pilot,
  pilotActive,
  pilotPlaying,
  pilotLoading,
  subscribed,
  trialDays,
  onTogglePilot,
  onSelect,
}: {
  episodes: readonly EpisodeSummary[];
  episodeCount: number;
  pilot: EpisodeSummary | null;
  pilotActive: boolean;
  pilotPlaying: boolean;
  pilotLoading: boolean;
  subscribed: boolean;
  trialDays: number;
  onTogglePilot: () => void;
  onSelect: (episode: EpisodeSummary, state: EpisodeState) => void;
}) {
  const { hero, me } = useHeroData();
  const player = usePlayer();
  const actions = usePlayerActions();
  const episodeTrack = pilotActive && player.track?.kind === "episode" ? player.track : null;

  return <main className="hero-page hero-page--luca">
    <section className="luca-hero editorial-wrap">
      <div className="luca-portrait">
        <img src={hero.theme.portraitUrl ?? "/characters/luca.jpg"} alt={hero.name} />
        <div><span>Audio · Season {hero.currentSeason?.ordinal ?? 1}</span><strong>{hero.currentSeason?.title ?? "First Dinner"}</strong><small>{episodeCount} episodes · written for headphones</small></div>
      </div>
      <div className="luca-copy">
        <p className="editorial-kicker">After hours · Fiction, 18+</p>
        <h1>After the last table leaves, he keeps <em>one seat.</em></h1>
        <p className="editorial-lede">{hero.description}</p>
        <section id="episode-one" className="luca-inline-player" aria-label="Episode one player">
          <div className="luca-inline-player__head">
            <button type="button" onClick={onTogglePilot} disabled={!pilot || pilotLoading} aria-label={pilotPlaying ? "Pause episode one" : "Play episode one"}><Icon name={pilotPlaying ? "pause" : "play"} size={22} /></button>
            <div><span>Episode 1 · free · no account</span><strong>{pilot?.title ?? "The First Dinner"}</strong></div>
          </div>
          {episodeTrack ? <>
            <Scrubber currentTime={player.currentTime} duration={player.duration || episodeTrack.manifest.lengthSec} onSeek={actions.seek} disabled={player.status === "loading"} />
            <Transcript lines={episodeTrack.manifest.transcript} currentTime={player.currentTime} onSeek={actions.seek} />
          </> : <div className="luca-inline-player__preview"><span>“Come closer to the pass. Hear the butter?”</span><span>“Taste slowly. There is no hurry now.”</span><strong>Play to follow the synced transcript.</strong></div>}
          <div className="luca-inline-player__meta"><span>Synced transcript</span><i /> <span>Sleep timer in the full player</span><i /> <span>Resumes where you stopped</span></div>
        </section>
      </div>
    </section>

    <section className="luca-content editorial-wrap">
      <div className="luca-season">
        <p className="editorial-kicker">Season 1 · {episodeCount} episodes</p>
        <h2>First Dinner</h2>
        <EpisodesBlock><EpisodeList episodes={episodes} heroSlug={hero.slug} timeZone={me?.timezone} currentOrdinal={player.track?.kind === "episode" ? player.track.ordinal : null} playing={player.status === "playing"} onSelect={onSelect} /></EpisodesBlock>
      </div>
      <PricingDeck pricing={hero.pricing} trialDays={trialDays} subscribed={subscribed} chat={false} />
    </section>

    <section className="luca-details editorial-wrap">
      <article><h3>About Luca</h3><p>Thirty-eight. Exacting at the stove, warmer than his reserve suggests. He reveals the past through food rather than speeches.</p></article>
      <article><h3>How it sounds</h3><p>Close microphone, a kitchen after hours, a pan cooling and a knife set down. Slow, intimate and written for headphones.</p></article>
      <article><h3>What arrives</h3><p>A new dinner on schedule, short voice notes and quiet extras for nights when you want the room without the plot.</p></article>
    </section>
    <EditorialFooter heroName={hero.name} creatorName={hero.creator.name} creatorUrl={hero.creator.url} />
  </main>;
}

function TheoLanding({
  messages,
  repliesLeft,
  draft,
  subscribed,
  trialDays,
  onDraft,
  onReply,
  onSubmit,
  onOpenConversation,
}: {
  messages: readonly PreviewMessage[];
  repliesLeft: number;
  draft: string;
  subscribed: boolean;
  trialDays: number;
  onDraft: (value: string) => void;
  onReply: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onOpenConversation: () => void;
}) {
  const { hero, episodes } = useHeroData();
  const gated = repliesLeft === 0;
  const threadRef = useRef<HTMLDivElement>(null);
  const sent = THEO_REPLIES.length - repliesLeft;
  const quickReplies = sent === 0
    ? ["Open it.", "Why are you telling me?", "Whose handwriting?"]
    : ["Keep going.", "What do you see?", "I'm still here."];

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTo({ top: thread.scrollHeight, behavior: sent > 1 ? "smooth" : "auto" });
  }, [messages.length, sent]);

  return <main className="hero-page hero-page--theo">
    <section className="theo-hero editorial-wrap">
      <div className="theo-copy">
        <p className="editorial-kicker">Text story · The Orpheum Letters · {hero.currentSeason?.episodes ?? episodes.length} episodes</p>
        <h1>Thirty days to save a cinema. <em>One person he tells.</em></h1>
        <p className="editorial-lede">{hero.description}</p>
        <div className="theo-actions">
          <a className="theo-primary" href="#theo-preview">Reply to Theo — free</a>
          <button type="button" className="theo-full-chat" onClick={onOpenConversation}>Open full chat</button>
        </div>
        <span className="theo-free-note">25 preview replies need no account. The full Messenger keeps your story and choices.</span>
        <div className="theo-points">
          <div><strong>He remembers</strong><span>Your name, choices and what you tell him after midnight.</span></div>
          <div><strong>Voice notes</strong><span>Reply by speaking when typing feels like too much.</span></div>
          <div><strong>On a schedule</strong><span>New scenes arrive at the hour the author chose.</span></div>
        </div>
      </div>

      <section id="theo-preview" className="theo-phone" aria-label="Conversation preview">
        <header><Avatar src={hero.theme.portraitUrl} name={hero.name} size={42} /><div><strong>Theo Ward</strong><span>{gated ? "waiting for you" : `online · ${repliesLeft} free ${repliesLeft === 1 ? "reply" : "replies"} left`}</span></div><small>Ep. 1 · House Lights</small></header>
        <div className="theo-thread" ref={threadRef}><time>Tonight · 01:12</time>{messages.map((message, index) => <p className={`theo-message theo-message--${message.from}`} key={`${index}-${message.text}`}>{message.text}</p>)}</div>
        {!gated ? <form className="theo-composer" onSubmit={onSubmit}>
          <div>{quickReplies.map((reply) => <button type="button" onClick={() => onReply(reply)} key={reply}>{reply}</button>)}</div>
          <label><span className="sr-only">Write to Theo</span><input value={draft} onChange={(event) => onDraft(event.target.value)} placeholder="Write to Theo…" maxLength={280} /><button type="submit" aria-label="Send reply" disabled={!draft.trim()}><Icon name="send" size={19} /></button></label>
        </form> : <div className="theo-gate"><h2>He's still typing.</h2><p>Choose a plan to save this thread and continue into the full first episode.</p><a href="#theo-access">See the plans</a><button type="button" onClick={onOpenConversation}>Open my conversation</button></div>}
      </section>
    </section>

    <section className="theo-content editorial-wrap">
      <div className="theo-season">
        <p className="editorial-kicker">Season 1 · {hero.currentSeason?.episodes ?? episodes.length} episodes</p>
        <h2>The Orpheum Letters</h2>
        <div className="theo-episode-list">{episodes.map((episode) => <article key={episode.ordinal}><span>{String(episode.ordinal).padStart(2, "0")}</span><div><strong>{episode.title}</strong><small>{episode.ordinal === 1 ? "A fresh chalk mark leads to an impossible sixth reel." : episode.ordinal === 2 ? "Thirty letters turn a family mystery into a public promise." : "A hidden plan gives the Orpheum a future."}</small></div><em>{episode.free ? "Free" : episode.locked ? "Members" : "Available"}</em></article>)}</div>
      </div>
      <div id="theo-access"><PricingDeck pricing={hero.pricing} trialDays={trialDays} subscribed={subscribed} chat /></div>
    </section>
    <EditorialFooter heroName={hero.name} creatorName={hero.creator.name} creatorUrl={hero.creator.url} />
  </main>;
}

function PricingDeck({ pricing, trialDays, subscribed, chat }: { pricing: HeroPricing; trialDays: number; subscribed: boolean; chat: boolean }) {
  if (subscribed) return <section className="editorial-pricing editorial-pricing--active"><p className="editorial-kicker">Your access</p><h2>{chat ? "The thread is open." : "Your seat is waiting."}</h2><p>Your subscription is active. Continue from the exact point where you stopped.</p><Link to={chat ? href.chat() : href.listen()} className="editorial-pricing__cta">{chat ? "Open the conversation" : "Continue listening"}</Link></section>;

  const plans = [
    { id: "monthly" as const, name: "Monthly", price: formatCents(pricing.monthlyCents, pricing.currency), note: "Every released episode. Cancel any time." },
    { id: "yearly" as const, name: "Yearly", price: formatCents(pricing.yearlyCents, pricing.currency), note: `${trialDays > 0 ? `${trialDays}-day free trial. ` : ""}${formatCents(monthlyEquivalentCents(pricing.yearlyCents), pricing.currency)}/mo equivalent.` },
    ...(pricing.demoDayPass
      ? [{ id: "all_at_once" as const, name: "24-hour demo", price: formatCents(pricing.demoDayPass.priceCents, pricing.currency), note: "Test payment. Full access for 24 hours; no card is charged." }]
      : chat || pricing.allAtOnceCents === null
        ? []
        : [{ id: "all_at_once" as const, name: "Whole season", price: formatCents(pricing.allAtOnceCents, pricing.currency), note: "One payment for every episode in this season." }]),
  ];

  return <section className="editorial-pricing">
    <p className="editorial-kicker">{chat ? "Keep the thread" : "Keep the seat"}</p>
    <h2>Choose how to continue.</h2>
    <div className="editorial-plans">{plans.map((plan) => <Link to={href.plans({ plan: plan.id })} key={plan.id}><span>{plan.name}</span><strong>{plan.price}</strong><small>{plan.note}</small></Link>)}</div>
    <Link to={href.plans({ plan: "yearly" })} className="editorial-pricing__cta">{trialDays > 0 ? `Start ${trialDays}-day free trial` : "See access options"}</Link>
    <small className="editorial-pricing__fine">Cancel any time · Prices set by the creator · Fiction, 18+</small>
  </section>;
}

function EditorialFooter({ heroName, creatorName, creatorUrl }: { heroName: string; creatorName: string; creatorUrl: string }) {
  return <footer className="editorial-footer editorial-wrap"><span>{heroName} · a character on Scrile AI</span><a href={creatorUrl}>Made by {creatorName}</a></footer>;
}

interface Perk { icon: IconName; title: MessageKey; body: MessageKey; }
const AUDIO_PERKS: readonly Perk[] = [
  { icon: "episode", title: "hero.weekly.episode.title", body: "hero.weekly.episode.body" },
  { icon: "rubric", title: "hero.weekly.rubric.title", body: "hero.weekly.rubric.body" },
  { icon: "voice", title: "hero.weekly.voice.title", body: "hero.weekly.voice.body" },
];
const CHAT_PERKS: readonly Perk[] = [
  { icon: "transcript", title: "hero.weekly.chat.messages.title", body: "hero.weekly.chat.messages.body" },
  { icon: "voice", title: "hero.weekly.chat.voice.title", body: "hero.weekly.chat.voice.body" },
  { icon: "sparkle", title: "hero.weekly.chat.memory.title", body: "hero.weekly.chat.memory.body" },
];

function isVideo(url: string): boolean { return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url); }

function Trailer({ url, poster, playing, onToggle }: { url: string; poster: string | null; playing: boolean; onToggle: () => void }) {
  if (isVideo(url)) return <video className="trailer-video" src={url} controls playsInline preload="metadata" {...(poster ? { poster } : {})} />;
  return <button type="button" className="trailer" onClick={onToggle} aria-pressed={playing}><span className="trailer__icon"><Icon name={playing ? "pause" : "play"} size={18} /></span><span className="trailer__label">{playing ? t("hero.trailerPause") : t("hero.trailer")}</span><span className="trailer__wave" aria-hidden="true">{Array.from({ length: 18 }, (_, index) => <i key={index} style={{ height: `${30 + ((index * 37) % 70)}%` }} />)}</span></button>;
}
